import { useMemo } from "react";
import { Disclosure, DisclosureSet } from "./disclosure.types";
import { useGetPublicCurrentLegalDocumentQuery } from "src/store/features/api/generated";
import { legalDocumentToDisclosure } from "src/utils/hooks/useLegalDisclosure";

// Set structure/copy is app config (not legal text); the disclosure DOCUMENTS are
// fetched from the public legal-document endpoint (signup runs pre-auth).
const COMPLETION = {
  title: "Thank you for reading and acknowledging our terms & conditions.",
  subtitle: "",
  buttonText: "Continue to sign up",
};

/** 404 = no disclosure for this jurisdiction; any other error is undetermined. */
const isNotFoundError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  (error as { status?: unknown }).status === 404;

/** Signup DisclosureSet (ToB + the region's state disclosure, if published) from the public endpoint. */
export function usePublicDisclosureSet(region: string | undefined): {
  disclosureSet: DisclosureSet | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const tob = useGetPublicCurrentLegalDocumentQuery({
    type: "termsOfBusinessDisclosure",
    country: "US",
  });
  const state = useGetPublicCurrentLegalDocumentQuery(
    { type: "state-disclosure", country: "US", region },
    { skip: !region },
  );

  // Resolved "no state disclosure for this region" — a valid outcome, not a failure
  const stateNotRequired = Boolean(region) && state.isError && isNotFoundError(state.error);

  const disclosureSet = useMemo<DisclosureSet | null>(() => {
    const disclosures: Disclosure[] = [];
    // Bail rather than degrade to a set missing a required doc
    const tobDisclosure = tob.data ? legalDocumentToDisclosure(tob.data) : null;
    if (!tobDisclosure) return null;
    disclosures.push(tobDisclosure);
    if (region && !stateNotRequired) {
      const stateDisclosure = state.data ? legalDocumentToDisclosure(state.data) : null;
      if (!stateDisclosure) return null;
      disclosures.push(stateDisclosure);
    }
    return {
      id: region ?? "default",
      name: region ?? "default",
      description: "",
      disclosures,
      completionTitle: COMPLETION.title,
      completionSubtitle: COMPLETION.subtitle,
      completionButtonText: COMPLETION.buttonText,
    };
  }, [region, stateNotRequired, tob.data, state.data]);

  const isLoading = tob.isLoading || (Boolean(region) && state.isLoading);
  const isError = tob.isError || (Boolean(region) && state.isError && !stateNotRequired);
  const refetch = () => {
    tob.refetch();
    if (region) state.refetch();
  };

  return { disclosureSet, isLoading, isError, refetch };
}
